import { ReactNode } from 'react';
import { IntlShape } from 'react-intl';
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import { CardProps } from '@designsystem/stories/03-molecules/Card';
import { MediaProps } from '@designsystem/stories/02-atoms/Media';
import {
  NormalizableContent,
  CardType,
  CardStyle,
  CARD_TYPES,
  RegisterContent,
} from './types';
import { formatInfoItem, createMemoCache, extractSubjectTags } from './utils';
import { normalizeContent, normalizeLink } from './normalizeContent';
import { CardAdapterStrategy } from './CardAdapterStrategy';
import { ImageSizes } from './types';

// Create memoized versions of expensive operations
const memoizedNormalizeContent = createMemoCache(
  ({ item, imageSizes }: { item: any; imageSizes?: ImageSizes }) =>
    normalizeContent(item, true, imageSizes),
  ({ item, imageSizes }: { item: any; imageSizes?: ImageSizes }) =>
    `${item?.['@id']}-${item?.['@type']}-${JSON.stringify(item?.title)}-${JSON.stringify(imageSizes)}`,
);

const memoizedNormalizeLink = createMemoCache(
  (item: Content) => normalizeLink(item),
  (item) => `${item?.['@id']}-${item?.['@type']}-${item?.mime_type}-link`,
);

/**
 * Transforms a single content item into a card format
 *
 * @param item - The content item to transform
 * @param intl - Internationalization object
 * @param type - Optional card type override
 * @param style - Optional card style
 * @param university - Optional university context
 * @param pushSubjectsAsTags - Whether to include subjects as tags
 * @param imageSizes - Optional image sizes for responsive images
 * @param onAnmelden - Optional callback invoked when the registration panel button is clicked
 * @returns Transformed card props
 */
export const transformToCard = (
  item: NormalizableContent,
  intl: IntlShape,
  type?: CardType | 'register-event' | 'none',
  style?: CardStyle,
  university?: NormalizableContent,
  pushSubjectsAsTags?: boolean,
  imageSizes?: ImageSizes,
  onAnmelden?: (item: RegisterContent) => void,
): CardProps => {
  // Early return for invalid items
  if (!item || !intl) {
    return createDefaultCard();
  }

  // Determine card type
  const cardType = determineCardType(item, type);

  // Create base card structure
  const baseCard = createBaseCard(item, cardType, style, imageSizes);

  // Apply type-specific transformation
  const transformedCard = applyCardTransformation(
    item,
    baseCard,
    intl,
    type || cardType,
    university,
    imageSizes,
    onAnmelden,
  );

  // Add subject tags if requested
  if (pushSubjectsAsTags === true) {
    const tags = extractSubjectTags(item);
    if (tags.length > 0) {
      transformedCard.tags = [...(transformedCard?.tags || []), ...tags];
    }
  }

  // Format info items if present
  if (transformedCard?.info) {
    transformedCard.info = transformedCard.info
      .map((infoItem) => formatInfoItem(infoItem, intl))
      .filter((infoItem) => !!infoItem.value);
  }

  // Apply category override if specified
  if (item?.category) {
    const transformedCategory = transformContentValue(item.category, intl);
    if (typeof transformedCategory === 'string') {
      transformedCard.category = transformedCategory;
    }
  }

  return transformedCard;
};

/**
 * Transforms multiple content items into card format
 *
 * @param items - Array of content items to transform
 * @param intl - Internationalization object
 * @param type - Optional card type override
 * @param style - Optional card style
 * @param university - Optional university context
 * @param pushSubjectsAsTags - Whether to include subjects as tags
 * @param imageSizes - Optional image sizes for responsive images
 * @param onAnmelden - Optional callback invoked when the registration panel button is clicked
 * @returns Array of transformed card props
 */
export const transformToCards = (
  items: NormalizableContent[],
  intl: IntlShape,
  type?: CardType | 'register-event' | 'none',
  style?: CardStyle,
  university?: NormalizableContent,
  pushSubjectsAsTags?: boolean,
  imageSizes?: ImageSizes,
  onAnmelden?: (item: RegisterContent) => void,
): CardProps[] => {
  // Early return for invalid input
  if (!Array.isArray(items) || items.length === 0 || !intl) {
    return [];
  }

  // Use map for better performance than forEach + push
  return items.map((item) =>
    transformToCard(
      item,
      intl,
      type,
      style,
      university,
      pushSubjectsAsTags,
      imageSizes,
      onAnmelden,
    ),
  );
};

/**
 * Creates a default card for error cases
 */
const createDefaultCard = (): CardProps => ({
  type: 'product',
  title: '',
});

/**
 * Determines the appropriate card type for an item
 */
const determineCardType = (
  item: NormalizableContent,
  typeOverride?: CardType | 'register-event' | 'none',
): CardType => {
  if (typeOverride === 'none') {
    return 'product';
  }

  if (typeOverride === 'register-event') {
    return 'register';
  }

  if (typeOverride) {
    return typeOverride;
  }

  const contentType = item?.['@type'];
  if (contentType && contentType in CARD_TYPES) {
    return CARD_TYPES[contentType];
  }

  return 'product';
};

/**
 * Creates the base card structure from content
 */
const createBaseCard = (
  item: NormalizableContent,
  cardType: CardType,
  style?: CardStyle,
  imageSizes?: ImageSizes,
): CardProps => {
  // Generate link FIRST using original item (before transformation)
  // External object-browser entries carry only {'@id': url, title} — no
  // @type — so the link must not require one (#1536).
  let link: string | undefined;
  if (item?.['@id']) {
    try {
      if (item['@type'] === 'Image' && item?.link) {
        link = normalizeLink(item?.link);
      } else {
        link = memoizedNormalizeLink(item as unknown as Content);
      }
    } catch {
      // Ignore link transformation errors
      link = undefined;
    }
  }

  // Then transform content
  const transformedContent = memoizedNormalizeContent({ item, imageSizes });

  // Handle string return from normalizeContent
  if (typeof transformedContent === 'string') {
    const baseCard: CardProps = {
      type: cardType,
      uid: item?.['@id'] as string | undefined,
      style: style,
      title: item.title || '',
      description: transformedContent,
    };

    // Add link if available
    if (link) {
      baseCard.link = link;
    }

    return baseCard;
  }

  // Handle object return from normalizeContent
  const contentObj = transformedContent as any;
  const baseCard: CardProps = {
    type: cardType,
    // Identity of the content itself — stays distinct for several Occasions of
    // the same event, which all share the parent event's link.
    uid: item?.['@id'] as string | undefined,
    style: style,
    title: contentObj?.teaser_title || contentObj?.title || item.title || '',
    description:
      contentObj?.teaser_text || contentObj?.description || item.description,
  };

  if (style !== 'table') {
    baseCard.image = contentObj?.image as MediaProps | undefined;
  }

  // Add link if available
  if (link) {
    baseCard.link = link;
  }

  return baseCard;
};

/**
 * Applies type-specific transformation using the strategy pattern
 */
const applyCardTransformation = (
  item: NormalizableContent,
  card: CardProps,
  intl: IntlShape,
  cardType: CardType | 'register-event' | 'none',
  university?: NormalizableContent,
  imageSizes?: ImageSizes,
  onAnmelden?: (item: RegisterContent) => void,
): CardProps => {
  const adapter = CardAdapterStrategy.getAdapter(cardType);

  if (adapter) {
    return adapter(item, card, intl, university, imageSizes, onAnmelden);
  }

  return card;
};

/**
 * Safely transforms content values (string or ReactNode)
 */
const transformContentValue = (
  value: string | ReactNode,
  intl: IntlShape,
): string | ReactNode => {
  if (typeof value === 'string') {
    const transformed = normalizeContent(value, true);
    return typeof transformed === 'string' ? transformed : value;
  }
  return value;
};

// Re-export types for external use
export type { NormalizableContent, CardType };

// Re-export utility functions that might be used externally
export {
  stringOrUndefined,
  formatInfoItem,
  isOccasionVisible as occasionVisible,
  findNextOccasion as nextOccasion,
} from './utils';
